import { Tuple }		from "ts-base/tuple";
import { Fn, Fn2 }		from "ts-base/fn";
import { Is }			from "ts-base/is";
import { Nea }			from "ts-base/nea";
import { Maybe }		from "ts-base/maybe";
import { Either }		from "ts-base/either";
import { Validated }	from "ts-base/validated";
import { Sync }			from "ts-base/sync";
import { Async }		from "ts-base/async";
import { Zio }			from "ts-base/zio";
import { Predicate }	from "ts-base/predicate";
import { Comparator }	from "ts-base/comparator";
import { Compared }		from "ts-base/compared";
import { Equal }		from "ts-base/equal";
import { Monoid }		from "ts-base/monoid";

export type Nullable<T>	= T|null;

export namespace Nullable {
	export type Value<T>	= T & {};

	export const when	= <T>(value:T) => (condition:boolean):Nullable<T>	=>
		condition ? value : null;

	export const test	= <T>(value:T) => (pred:Predicate<T>):Nullable<T> =>
		when(value)(pred(value));

	export const valueIf	= <T>(condition:boolean, value:T):Nullable<T>	=>
		when(value)(condition);

	//------------------------------------------------------------------------------

	export const orElse	= <T>(a:Nullable<T>) => (b:Nullable<T>):Nullable<T>	=>
		a !== null ? a : b;

	export const unsafeGet	= <T>(self:Nullable<T>):T	=> {
		if (self === null)	throw new Error("unexpected null");
		return self;
	};

	export const getOr	= <T>(self:Nullable<T>) => <U>(empty:U):T|U	=>
		self !== null ? self : empty;

	export const mapOr	= <S>(self:Nullable<S>) => <T>(func:Fn<S, T>) => (empty:T):T =>
		self !== null ? func(self) : empty;

	//------------------------------------------------------------------------------

	export const includes	= <T>(self:Nullable<T>) => (value:T):boolean	=>
		exists(self)(it => it === value);

	export const exists	= <T>(self:Nullable<T>) => (pred:Predicate<T>):boolean =>
		self !== null && pred(self);

	export const forall	= <T>(self:Nullable<T>) => (pred:Predicate<T>):boolean =>
		self === null || pred(self);

	export const some	= exists;
	export const every	= forall;

	//------------------------------------------------------------------------------

	export const filter	= <T>(self:Nullable<T>) =>(pred:Predicate<T>):Nullable<T>	=>
		self !== null && pred(self) ? self : null;

	export const filterNot	= <T>(self:Nullable<T>) => (pred:Predicate<T>):Nullable<T>	=>
		self !== null && !pred(self) ? self : null;

	export const filterIs	= <T>(self:Nullable<T>) => <U extends T>(is:Is<T, U>):Nullable<U>	=>
		self !== null && is(self) ? self : null;

	//------------------------------------------------------------------------------

	/*
	export const toArray	= <T>(self:Nullable<T>):T[]	=>
		self !== null ? [ self ] : [];

	export const toSet	= <T>(self:Nullable<T>):Set<T>	=>
		self !== null ? new Set([ self ]) : new Set();
	*/

	//------------------------------------------------------------------------------

	export const forEach	= <T>(self:Nullable<T>)	=> (handler:Fn<T, void>):void	=> {
		if (self !== null)	handler(self);
	};

	//------------------------------------------------------------------------------
	//## pseudo applicative functor

	/*
	// NOTE this was not a lift (a flipped map) but a flipped then
	export const lift	= <S, T>(func:Fn<S, Nullable<T>>):Fn<Nullable<S>, Nullable<T>>	=>
		(it) => it !== null ? func(it) : null;
	*/

	// this allows a nullable return value, so it's more like a flatMap2 than a map2
	export const combine	= <S1, S2>(s1:Nullable<S1>, s2:Nullable<S2>) => <T>(func:Fn2<S1, S2, Nullable<T>>):Nullable<T>	=>
		s1 !== null && s2 !== null ? func(s1, s2) : null;

	export const combineN	= <S1, S extends ReadonlyArray<Nullable<unknown>>>(arg:Nullable<S1>, ...args:S) => <T>(func:(arg:S1, ...args:ValueN<S>)=>Nullable<T>):Nullable<T>	=> {
		if (arg === null)					return null;
		if (args.some(it => it === null))	return null;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return func(arg, ...args as any);
	};

	//------------------------------------------------------------------------------
	//## pseudo applicative functor

	export const map2	= <S1, S2>(s1:Nullable<S1>, s2:Nullable<S2>) => <T>(func:Fn2<S1, S2, T>):Nullable<T> =>
		s1 !== null && s2 !== null ? func(s1, s2) :	null;

	export const tupleN	= <T1, T extends ReadonlyArray<Nullable<unknown>>>(arg:Nullable<T1>, ...args:T):Nullable<Tuple.Prepend<T1, ValueN<T>>>	=>
		mapN(arg, ...args)(Tuple.ofN);

	export const mapN	= <S1, S extends ReadonlyArray<Nullable<unknown>>>(arg:Nullable<S1>, ...args:S) => <T>(func:(arg:S1, ...args:ValueN<S>)=>T):Nullable<T>	=> {
		if (arg === null)					return null;
		if (args.some(it => it === null))	return null;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return func(arg, ...args as any);
	};

	type ValueN<T>	= { [P in keyof T]:Value<T[P]> };

	export type SelfRecord<T>	= {
		[K in keyof T]: Nullable<T[K]>
	};

	export const object	= <T>(input:SelfRecord<T>):Nullable<Readonly<T>>	=> {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const out:any	= {};
		for (const key in input) {
			const part	= input[key];
			if (part === null)	return null;
			out[key]	= part;
		}
		return out;
	};

	//------------------------------------------------------------------------------
	//## pseudo monad

	/** almost flatMap, but disagrees with the applicative functor */
	export const then	= <S>(it:Nullable<S>) => <T>(func:Fn<S, Nullable<T>>):Nullable<T>	=>
		it !== null ? func(it) : null;

	export const andThen	= <A, B, C>(ab:Fn<A, Nullable<B>>, bc:Fn<B, Nullable<C>>):Fn<A, Nullable<C>>	=>
		a => then(ab(a))(bc);

	export const andThen3	= <A, B, C, D>(ab:Fn<A, Nullable<B>>, bc:Fn<B, Nullable<C>>, cd:Fn<C, Nullable<D>>):Fn<A, Nullable<D>>	=>
		andThen(ab, andThen(bc, cd));

	export const andThen4	= <A, B, C, D, E>(ab:Fn<A, Nullable<B>>, bc:Fn<B, Nullable<C>>, cd:Fn<C, Nullable<D>>, de:Fn<D, Nullable<E>>):Fn<A, Nullable<E>>	=>
		andThen(ab, andThen(bc, andThen(cd, de)));

	//-----------------------------------------------------------------------------
	//## traversal

	export const traverseKleisli	= <S>(self:Nullable<S>) => <C, T>(func:Fn<S, Fn<C, T>>):Fn<C, Nullable<T>>	=>
		(t:C) => self !== null ? func(self)(t) : null;

	export const sequenceKleisli	= <C, T>(self:Nullable<Fn<C, T>>):Fn<C, Nullable<T>>	=>
		traverseKleisli(self)(Fn.identity);

	export const traverseArray	= <S>(self:Nullable<S>) => <T>(func:Fn<S, ReadonlyArray<T>>):Array<Nullable<T>>	=>
		self !== null ? [ ...func(self) ] : [ null ];

	export const sequenceArray	= <T>(self:Nullable<Array<T>>):Array<Nullable<T>>	=>
		traverseArray(self)(Fn.identity);

	export const traverseNea	= <S>(self:Nullable<S>) => <T>(func:Fn<S, Nea<T>>):Nea<Nullable<T>>	=>
		self !== null ? func(self) : Nea.single(null);

	export const sequenceNea	= <T>(self:Nullable<Nea<T>>):Nea<Nullable<T>>	=>
		traverseNea(self)(Fn.identity);

	export const traverseMaybe	= <S>(self:Nullable<S>) => <T>(func:Fn<S, Maybe<T>>):Maybe<Nullable<T>>	=>
		self !== null ? func(self) : Maybe.some(null);

	export const sequenceMaybe	= <T>(self:Nullable<Maybe<T>>):Maybe<Nullable<T>>	=>
		traverseMaybe(self)(Fn.identity);

	export const traverseEither	= <S>(self:Nullable<S>) => <E, T>(func:Fn<S, Either<E, T>>):Either<E, Nullable<T>>	=>
		self !== null ? func(self) : Either.right(null);

	export const sequenceEither	= <E, T>(self:Nullable<Either<E, T>>):Either<E, Nullable<T>>	=>
		traverseEither(self)(Fn.identity);

	export const traverseValidated	= <S>(self:Nullable<S>) => <E, T>(func:Fn<S, Validated<E, T>>):Validated<E, Nullable<T>>	=>
		self !== null ? func(self) : Validated.valid(null);

	export const sequenceValidated	= <E, T>(self:Nullable<Validated<E, T>>):Validated<E, Nullable<T>>	=>
		traverseValidated(self)(Fn.identity);

	export const traversePromise	= <S>(self:Nullable<S>) => <T>(func:Fn<S, Promise<T>>):Promise<Nullable<T>>	=>
		self !== null ? func(self) : Promise.resolve(null);

	export const sequencePromise	= <T>(self:Nullable<Promise<T>>):Promise<Nullable<T>>	=>
		traversePromise(self)(Fn.identity);

	export const traverseSync	= <S>(self:Nullable<S>) => <T>(func:Fn<S, Sync<T>>):Sync<Nullable<T>>	=>
		() => self !== null ? func(self)() : null;

	export const sequenceSync	= <T>(self:Nullable<Sync<T>>):Sync<Nullable<T>>	=>
		traverseSync(self)(Fn.identity);

	export const traverseAsync	= <S>(self:Nullable<S>) => <T>(func:Fn<S, Async<T>>):Async<Nullable<T>>	=>
		() => self !== null ? func(self)() : Promise.resolve(null);

	export const sequenceAsync	= <T>(self:Nullable<Async<T>>):Async<Nullable<T>>	=>
		traverseAsync(self)(Fn.identity);

	export const traverseZio		= <S>(self:Nullable<S>) => <C, T>(func:Fn<S, Zio<C, T>>):Zio<C, Nullable<T>>	=>
		(c:C)	=> traversePromise(self)((it) => func(it)(c));

	export const sequenceZio	= <C, T>(self:Nullable<Zio<C, T>>):Zio<C, Nullable<T>>	=>
		traverseZio(self)(Fn.identity);

	//-----------------------------------------------------------------------------
	//## monoid

	export const fold	= <T>(self:Nullable<T>) => (monoid:Monoid<T>):T	=>
		self !== null ? self : monoid.empty;

	export const foldMap	= <S>(self:Nullable<S>) => <T>(func:Fn<S, T>) => (monoid:Monoid<T>):T	=>
		self !== null ? func(self) : monoid.empty;

	//-----------------------------------------------------------------------------
	//## typeclasses

	export const equalOf	= <T>(base:Equal<T>):Equal<T|null>	=> (a:T|null, b:T|null):boolean	=>
		a === null && b === null	? true	:
		a === null					? false	:
		b === null					? false	:
		base(a, b);

	export const equalOfBuiltin	= <T>():Equal<T|null>	=>
		equalOf(Equal.builtin);

	export const compareNullFirst	= <T>(base:Comparator<T>):Comparator<T|null>	=> (a:T|null, b:T|null):Compared	=>
		a === null && b === null	? 0		:
		a === null					? -1	:
		b === null					? +1	:
		base(a, b);

	export const compareNullLast	= <T>(base:Comparator<T>):Comparator<T|null>	=> (a:T|null, b:T|null):Compared	=>
		a === null && b === null	? 0		:
		a === null					? +1	:
		b === null					? -1	:
		base(a, b);
}
